import { formatDay, formatTime, formatWeekdayTime, tt } from '@agos/shared'

/**
 * How long a ticket has been sitting, and how long until a pre-order is due.
 *
 * The colour is the whole point of the kitchen display: a cook glances across
 * the room and reads red before they read anything else. The four bands are
 * ported unchanged from legacy `pages/kitchen.tsx`.
 *
 * Every function here takes `now` rather than calling `Date.now()`, so the
 * screen measures against the moment the list arrived rather than against the
 * clock — FB-039, found on the pre-order board and the same bug here.
 */

const MINUTE_MS = 60_000

export type ElapsedBand = 'fresh' | 'warming' | 'late' | 'overdue'

const BAND_MINUTES: Array<[ElapsedBand, number]> = [
  ['fresh', 5],
  ['warming', 10],
  ['late', 15],
]

export const ELAPSED_TEXT_COLORS: Record<ElapsedBand, string> = {
  fresh: 'text-green-400',
  warming: 'text-yellow-400',
  late: 'text-orange-400',
  overdue: 'text-red-400',
}

export const ELAPSED_BG_COLORS: Record<ElapsedBand, string> = {
  fresh: 'bg-green-500/10',
  warming: 'bg-yellow-500/10',
  late: 'bg-orange-500/10',
  overdue: 'bg-red-500/10',
}

export function elapsedMinutes(since: string, now: number): number {
  return Math.floor((now - new Date(since).getTime()) / MINUTE_MS)
}

export function elapsedBand(since: string, now: number): ElapsedBand {
  const minutes = elapsedMinutes(since, now)
  for (const [band, limit] of BAND_MINUTES) {
    if (minutes < limit) return band
  }
  return 'overdue'
}

/** `< 1 min`, `7 min`, `1h 20m`. */
export function elapsedFormatted(since: string, now: number): string {
  const minutes = elapsedMinutes(since, now)
  if (minutes < 1) return '< 1 min'
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

export function minutesUntil(target: string, now: number): number {
  return Math.floor((new Date(target).getTime() - now) / MINUTE_MS)
}

/** `Teraz!`, `25 min`, `2h 5m`. */
export function formatCountdown(minutes: number): string {
  if (minutes <= 0) return tt('Teraz!')
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/**
 * Whether a pre-order is close enough to be cooked now.
 *
 * An order with no scheduled time is for now by definition. The threshold is
 * the franchise's `kitchenDisplayInterval`: everything further out belongs on
 * the dispatch board, not on a card taking up a cook's screen.
 */
export function isPreorderDueNow(
  scheduledAt: string | null | undefined,
  thresholdMinutes: number,
  now: number,
): boolean {
  if (!scheduledAt) return true
  return minutesUntil(scheduledAt, now) <= thresholdMinutes
}

/**
 * When a queued pre-order is due, for the §9.318 section - the owner's Q8.
 *
 * **Today's says the time, a later day says the weekday and the date**, because *"12:00"* on a card
 * with no day is the one thing that could make a cook start tomorrow's order tonight. Legacy makes
 * the same distinction on its dispatch card (`dispatch.tsx:643`) and its kitchen queue.
 *
 * **Formatted through `@agos/shared`, never `toLocaleTimeString`.** `eslint` refused the first
 * version under FRONTEND-BUGS N-01 and was right: those follow the **device's** zone, so a tablet
 * set to another one would put a Bratislava evening on the wrong day - which is the whole failure
 * this card exists to prevent. `formatWeekdayTime` was already there, documented *"for the pre-order
 * board"*.
 *
 * The countdown comes with it while it is under two hours, which is when it starts mattering.
 */
export function preorderWhen(scheduledAt: string | null | undefined, now: number): string {
  if (!scheduledAt) return ''
  const when = new Date(scheduledAt)
  if (Number.isNaN(when.getTime())) return ''

  const minutes = minutesUntil(scheduledAt, now)
  const countdown = minutes > 0 && minutes < 120 ? ` · ${formatCountdown(minutes)}` : ''
  // The same day in the **restaurant's** zone, which is what `formatDay` answers for both.
  const sameDay = formatDay(when) === formatDay(new Date(now))
  return sameDay
    ? `${tt('Dnes')} ${formatTime(when)}${countdown}`
    : `${formatWeekdayTime(when)}${countdown}`
}

export interface SortableTicket {
  status?: string
  type?: string
  createdAt?: string
}

/**
 * The order the cards are laid out in: what is already being made first, then
 * the restaurant's own tables, then oldest first.
 *
 * Restaurant before delivery is not arbitrary — a seated guest is waiting at a
 * table in the room, and a delivery has a driver's travel time absorbing a few
 * minutes of slack.
 */
export function compareKitchenTickets(left: SortableTicket, right: SortableTicket): number {
  const byStatus = (ticket: SortableTicket) => (ticket.status === 'preparing' ? 0 : 1)
  if (byStatus(left) !== byStatus(right)) return byStatus(left) - byStatus(right)

  const byType = (ticket: SortableTicket) => (ticket.type === 'restaurant' ? 0 : 1)
  if (byType(left) !== byType(right)) return byType(left) - byType(right)

  return new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime()
}

export type ItemProgress = 'idle' | 'working' | 'done'

/**
 * Tapping a line cycles it: untouched → being made → made → untouched.
 *
 * Local to the device on purpose. It is a cook's own scratch mark on their own
 * screen, and legacy never sends it anywhere — two cooks on two tablets each
 * keep their own.
 */
export function nextItemProgress(current: ItemProgress): ItemProgress {
  if (current === 'done') return 'idle'
  if (current === 'working') return 'done'
  return 'working'
}
